import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { Dialog, IconButton, Typography } from '@mui/material';
import { useCallback, useRef, useState, type PointerEvent, type WheelEvent } from 'react';

import { cn } from '@/shared/lib/cn';

const MIN_SCALE = 1;
const MAX_SCALE = 6;
/** One press of a zoom button. Small enough to frame a signature precisely. */
const BUTTON_STEP = 0.5;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

interface Transform {
  scale: number;
  x: number;
  y: number;
}

const IDENTITY: Transform = { scale: MIN_SCALE, x: 0, y: 0 };

/**
 * Keeps the point under the fingers (or cursor) pinned while the scale changes,
 * so zooming feels like it grabs the image rather than the viewport centre.
 */
const zoomAround = (
  current: Transform,
  nextScale: number,
  originX: number,
  originY: number,
) => {
  const scale = clamp(nextScale, MIN_SCALE, MAX_SCALE);
  const ratio = scale / current.scale;

  return {
    scale,
    // At 1x the image is centred by the flex container, so any accumulated pan
    // would leave it visibly off-centre with nowhere to drag it back.
    x: scale === MIN_SCALE ? 0 : originX - (originX - current.x) * ratio,
    y: scale === MIN_SCALE ? 0 : originY - (originY - current.y) * ratio,
  };
};

interface ImageViewerDialogProps {
  open: boolean;
  onClose: () => void;
  src: string;
  alt: string;
  /** Shown in the header bar so the viewer says which image is open. */
  title: string;
  /**
   * Renders the image on a white plate instead of directly on the stage.
   * For a transparent PNG — a signature — the tinted stage would otherwise
   * read as the image's own background.
   */
  onPlate?: boolean;
}

/**
 * Full-screen image viewer with pinch, wheel and button zoom.
 *
 * The zoom buttons are not a fallback for pinch — they are the only zoom
 * affordance a director can actually see. Pinch and scroll-wheel are the
 * shortcuts for people who already expect them.
 */
export const ImageViewerDialog = ({
  open,
  onClose,
  src,
  alt,
  title,
  onPlate = false,
}: ImageViewerDialogProps) => (
  // The label goes on the PAPER, not on the Dialog: MUI spreads a Dialog's own
  // props onto the outer `role="presentation"` wrapper, so `aria-label` there
  // never reaches the `role="dialog"` element and leaves it unnamed.
  <Dialog
    open={open}
    onClose={onClose}
    fullScreen
    slotProps={{ paper: { 'aria-label': title } }}
  >
    {/* Mounted only while open, so zoom and pan reset on close by unmounting
        rather than by an effect that fights the render cycle. */}
    {open && (
      <ImageViewerSurface
        onClose={onClose}
        src={src}
        alt={alt}
        title={title}
        onPlate={onPlate}
      />
    )}
  </Dialog>
);

type ImageViewerSurfaceProps = Omit<ImageViewerDialogProps, 'open'>;

const ImageViewerSurface = ({ onClose, src, alt, title, onPlate }: ImageViewerSurfaceProps) => {
  const [transform, setTransform] = useState<Transform>(IDENTITY);
  const surfaceRef = useRef<HTMLDivElement>(null);
  /** Live pointers, so one finger pans and two pinch without extra state. */
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinchStart = useRef<{ distance: number; scale: number } | null>(null);

  /** Pointer coordinates relative to the surface centre — the transform origin. */
  const toSurfaceSpace = useCallback((clientX: number, clientY: number) => {
    const bounds = surfaceRef.current?.getBoundingClientRect();
    if (bounds === undefined) return { x: 0, y: 0 };

    return {
      x: clientX - (bounds.left + bounds.width / 2),
      y: clientY - (bounds.top + bounds.height / 2),
    };
  }, []);

  const zoomByStep = (direction: 1 | -1) => {
    setTransform((current) =>
      zoomAround(current, current.scale + direction * BUTTON_STEP, 0, 0),
    );
  };

  const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
    const origin = toSurfaceSpace(event.clientX, event.clientY);

    setTransform((current) =>
      zoomAround(
        current,
        current.scale * (event.deltaY < 0 ? 1.15 : 1 / 1.15),
        origin.x,
        origin.y,
      ),
    );
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.current.size === 2) {
      const [first, second] = [...pointers.current.values()];
      if (first === undefined || second === undefined) return;

      const distance = Math.hypot(second.x - first.x, second.y - first.y);
      // Two fingers landing on the same pixel give a zero baseline, and the
      // ratio in `handlePointerMove` divides by it — that produces NaN, which
      // clamps to NaN and leaves the image with `scale(NaN)`, invisible and
      // unrecoverable because Reset is disabled at a non-greater-than scale.
      if (distance === 0) return;

      pinchStart.current = { distance, scale: transform.scale };
    }
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const previous = pointers.current.get(event.pointerId);
    if (previous === undefined) return;

    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.current.size >= 2) {
      const [first, second] = [...pointers.current.values()];
      const start = pinchStart.current;
      if (first === undefined || second === undefined || start === null) return;

      const distance = Math.hypot(second.x - first.x, second.y - first.y);
      const origin = toSurfaceSpace((first.x + second.x) / 2, (first.y + second.y) / 2);

      setTransform((current) =>
        zoomAround(current, (start.scale * distance) / start.distance, origin.x, origin.y),
      );
      return;
    }

    // Panning a signature that already fits the screen would just slide it out
    // of view, so dragging only moves the image once it is zoomed in.
    if (transform.scale === MIN_SCALE) return;

    const dx = event.clientX - previous.x;
    const dy = event.clientY - previous.y;

    setTransform((current) => ({ ...current, x: current.x + dx, y: current.y + dy }));
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
  };

  const isZoomed = transform.scale > MIN_SCALE;

  return (
    <div className="flex h-dvh flex-col bg-surface">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-divider px-2 py-2 pt-safe">
        <Typography variant="subtitle2" className="truncate pl-2">
          {title}
        </Typography>
        <IconButton aria-label="Close signature viewer" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>

      <div
        ref={surfaceRef}
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        // `touch-none` is what stops the browser panning the page mid-pinch.
        className={cn(
          'flex min-h-0 flex-1 touch-none items-center justify-center overflow-hidden bg-surface-subtle p-4',
          isZoomed ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in',
        )}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          style={{
            transform: `translate3d(${String(transform.x)}px, ${String(transform.y)}px, 0) scale(${String(transform.scale)})`,
          }}
          className={cn(
            'max-h-full max-w-full origin-center object-contain select-none',
            onPlate && 'rounded-lg bg-surface p-6 shadow-card',
          )}
        />
      </div>

      <div className="flex shrink-0 items-center justify-center gap-2 border-t border-divider px-4 py-2 pb-safe">
        <IconButton
          aria-label="Zoom out"
          onClick={() => {
            zoomByStep(-1);
          }}
          disabled={transform.scale <= MIN_SCALE}
        >
          <ZoomOutIcon />
        </IconButton>

        <Typography
          variant="body2"
          color="text.secondary"
          role="status"
          className="w-16 text-center tabular-nums"
        >
          {Math.round(transform.scale * 100)}%
        </Typography>

        <IconButton
          aria-label="Zoom in"
          onClick={() => {
            zoomByStep(1);
          }}
          disabled={transform.scale >= MAX_SCALE}
        >
          <ZoomInIcon />
        </IconButton>

        <IconButton
          aria-label="Reset zoom"
          onClick={() => {
            setTransform(IDENTITY);
          }}
          disabled={!isZoomed}
        >
          <RestartAltIcon />
        </IconButton>
      </div>
    </div>
  );
};
